import { ChangeDetectionStrategy, Component, computed, effect, inject, output, resource, signal, viewChild } from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ZaehlerStore } from './zaehler.store.service';
import { firstValueFrom } from 'rxjs';
import {
  MessungGroup,
  MessungModel,
  MesswertEintrag,
  MesswertErfassung,
  ToleranzenMinus,
  ToleranzenPlus,
  ZählerPrüfung,
  ZählerWert,
} from '../../_overlays/zaehlerverwaltung/zaehler.types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ZType,
  ZArtOptions,
  ZArtLongNames,
  MessungDropdownList,
  ZArt,
  AusfallStatus,
} from '../../_overlays/zaehlerverwaltung/zaehler.enums';
import { FilterTolByNameOptionsPipe } from '../../_overlays/zaehlerverwaltung/filter-tol-by-options.pipe';
import { TransformColNamesPipe } from '../../_overlays/zaehlerverwaltung/transform-col-names.pipe';
import { WerteRowValuesPipe } from '../../_overlays/zaehlerverwaltung/werte-row-values.pipe';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { FormControl, FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { HilfeBeschreibungModalComponent } from '../../_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { AusfallColorPipe } from './ausfall-color.pipe';
import { AddToleranzenPipe } from './add-toleranzen.pipe';
import { CommonModule } from '@angular/common';
import { AtIndexArrayPipe } from 'src/app/pipes/array-methods.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { ExcelService } from 'src/app/services/Shared/excel.service';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { Row } from 'src/app/models/ui/table';
import { APIService } from 'src/app/services/APIService/api.service';
import { FormGroupOfType } from 'src/app/types/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-zaehlertab',
  templateUrl: './zaehlertab.component.html',
  styleUrls: ['./zaehlertab.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    FilterTolByNameOptionsPipe,
    TransformColNamesPipe,
    WerteRowValuesPipe,
    HilfeBeschreibungModalComponent,
    DatalistInputComponent,
    AusfallColorPipe,
    AddToleranzenPipe,
    MrTranslatePipe,
    AtIndexArrayPipe,
    BackendLocaleDatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
})
export class ZaehlertabComponent {
  private readonly apiService = inject(ZaehlerApiService);
  private readonly sharedAPI = inject(APIService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly confirmService = inject(ConfirmService);
  private readonly excelService = inject(ExcelService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly store = inject(ZaehlerStore);

  readonly saveWartung = output<void>();

  private pruefungNgForm = viewChild('pruefungForm', { read: NgForm });

  protected readonly tol = this.apiService.getToleranzenBezeichnungen();
  private readonly zaehlerNameRs = this.apiService.getZaehlerByMode(this.store.verknüpfung, this.store.objekt);
  protected readonly zaehlerName = this.zaehlerNameRs.value;
  protected readonly skizzeRs = resource({
    params: () => this.zaehlerName()?.Bild || undefined,
    loader: ({ params: name }) => this.sharedAPI.getSkizzeByName(name),
  });

  private readonly SZAEHNID = computed(() => this.zaehlerName()?.ID);
  protected readonly werte_checklist = this.apiService.getZaehlerWerteBySZAEHNID(this.SZAEHNID);
  protected readonly checklist = computed(() => this.werte_checklist.value().rows);
  private readonly pruefungen = this.apiService.getPruefungenByObjektID(() => this.objekt()?.OSTAMMID);

  private readonly messungenRs = resource({
    params: () => {
      const pruefungen = this.pruefungen.value();
      const SZAEHNID = this.SZAEHNID();
      return pruefungen && SZAEHNID ? { pruefungen, SZAEHNID } : undefined;
    },
    loader: ({ params: { pruefungen, SZAEHNID } }) =>
      Promise.all(
        pruefungen.map(pruefung =>
          this.apiService
            .getZaehlerWerteByPruefungID(pruefung.OPRUEFID, SZAEHNID)
            .then(messwerte => ({ pruefung, messwerte }))
        )
      ),
  });

  protected readonly messungen = computed(() => {
    const groups = this.messungenRs.value();
    const checklist = this.checklist();
    if (!groups || !checklist) return [];
    let lastInd: number, lastDatum: string;
    const groups_mapped: MessungGroup[] = groups.flatMap((group, index) => {
      const { pruefung, messwerte } = group;
      if (!messwerte.length) return [];

      let Column_Datum = this.localeDate.transform(pruefung.P_ZDatum);

      if (Column_Datum == lastDatum) Column_Datum = `${Column_Datum} (${index - lastInd})`;
      else {
        lastInd = index;
        lastDatum = Column_Datum;
      }

      const formatted_pruefung: ZählerPrüfung = { ...pruefung, Column_Datum };
      const matched_messwerte = checklist.map(row => messwerte.find(wert => wert.Bezeichnung == row.Code));
      return [{ pruefung: formatted_pruefung, messwerte: matched_messwerte }];
    });

    return groups_mapped;
  });

  protected readonly edit_messung = signal<MessungGroup>(undefined);
  protected readonly index = signal<number>(0);

  protected readonly messenderList = toSignal(this.apiService.getZaehlerDropdownList(MessungDropdownList.Messender));
  protected readonly messgeraeteList = toSignal(this.apiService.getZaehlerDropdownList(MessungDropdownList.Messgerät));

  protected readonly isOpenModal = signal(false);
  protected readonly isHelperModalOpen = signal(false);
  protected readonly objekt = this.store.objekt;

  protected readonly erfassungForm = new FormGroupOfType<MesswertErfassung>({
    ID: new FormControl(null), //* readonly
    Bezeichnung: new FormControl(null), //* readonly
    Ist: new FormControl(null),
    Ausfall: new FormControl(null), //* readonly
    Bemerkung: new FormControl(null),
  });

  private isChanged: boolean;
  protected readonly ZType_Header = ZType.Header;
  protected readonly ZArt_Options = ZArtOptions;
  protected readonly ZArt_Map = ZArtLongNames;

  constructor() {
    this.store.onSave = OPRUEFID => {
      this.objekt().OPRUEFID = OPRUEFID;
      return this.saveIfValid();
    };
    effect(() => {
      if (this.zaehlerNameRs.status() != 'resolved') return;
      if (this.SZAEHNID()) {
        this.store.assignedZaehlerName.set(this.zaehlerName().Code);
      } else this.store.assignedZaehlerName.set(undefined);
    });

    effect(() => {
      // * edit_messung setzten:
      const defaultMessung = !this.store.isReadOnly() ? createMessungShape() : null;
      const editMessung: MessungGroup =
        !this.store.isNew() ? this.messungen().find(group => group.pruefung.OPRUEFID == this.objekt().OPRUEFID) : null;
      this.edit_messung.set(editMessung ?? defaultMessung);
    });

    this.erfassungForm.controls.Ist.valueChanges.pipe(takeUntilDestroyed()).subscribe(async ist => {
      const ausfall = await this.calcAusfall(ist, this.checklist()[this.index()]);
      this.erfassungForm.controls.Ausfall.setValue(ausfall);
    });
  }

  /**
   * @method fromTemplate, saves if Wartung is already saved with OPRUEFID or
   * @emits saveWartung
   * @method fromWartung, called from @see ZaehlerStore.onSave
   */
  protected async saveIfValid() {
    if (this.store.isNew() || !this.objekt().OPRUEFID) {
      this.pruefungNgForm().form.markAsDirty();
      this.saveWartung.emit();
      return;
    }
    if (this.pruefungNgForm().pristine && !this.isChanged) return true;

    if (this.pruefungNgForm().invalid) {
      this.toastr.warning(
        this.mrTranslate.transform('Datum oder Messender fehlen!'),
        this.mrTranslate.transform('Zähler')
      );
      return false;
    }

    try {
      const pack = this.isChanged || this.edit_messung().unsaved || this.pruefungNgForm().form.get('P_ZUser').dirty;
      const sendObj: MessungModel = {
        pruefung: this.edit_messung().pruefung,
        messwerte: pack ? await this.packEintraege() : null,
      };

      const { success } = await firstValueFrom(
        this.apiService.setZaehlerMessung(this.objekt().OPRUEFID, this.SZAEHNID(), sendObj)
      );
      if (success) {
        this.toastr.success(
          this.mrTranslate.transform('Erfolgreich gespeichert!'),
          this.mrTranslate.transform('Zähler')
        );
        this.isChanged = false;
        this.pruefungNgForm().form.markAsPristine();
        this.pruefungen.reload();
        this.store.toBeSaved.set(false);
      } else
        this.toastr.error(
          this.mrTranslate.transform('Etwas ist schief gelaufen'),
          this.mrTranslate.transform('Zähler')
        );

      return success;
    } catch (error) {
      if ((error as Error).message != 'Abbrechen') console.log(error);
      return false;
    }
  }

  private async packEintraege() {
    let setNG: boolean;
    const checklist = this.checklist();
    const messwerte = this.edit_messung().messwerte;
    const model: MesswertEintrag[] = [];

    for (let i = 0; i < checklist.length; i++) {
      const eintrag = createEintrag(messwerte[i], checklist[i]);
      if (!eintrag.isHeader && eintrag.Ist == null) {
        setNG ??= await this.confirmService.info(`
          ${this.mrTranslate.transform('Es wurden nicht alle Zählerwerte erfasst.')}
          ${this.mrTranslate.transform('Möchten Sie die Eingaben trotzdem speichen?')}
        `);

        if (setNG) eintrag.Ist = 'n.g.';
        else throw new Error('Abbrechen');
      }
      model[i] = eintrag;
    }

    return model;
  }

  protected async saveRow() {
    const model = this.erfassungForm.getRawValue();
    if (model.Ausfall == this.mrTranslate.transform('i.O.')) model.Ausfall = null;

    this.edit_messung.update(messung => {
      const wert = messung.messwerte[this.index()];
      messung.messwerte[this.index()] = { ...wert, ...model };
      return { ...messung };
    });

    this.isChanged = true;

    if ((await this.editNextRow()) == 'END') {
      this.isOpenModal.set(false);
      this.index.set(0);
    }
  }

  protected editPrevRow() {
    this.editNextRow(null, -1);
  }

  protected async editNextRow(index?: number, direction: 1 | -1 = 1): Promise<'END'> {
    const checklist = this.checklist();
    index ??= this.index() + direction;
    while (checklist[index]?.ZType == ZType.Header) {
      index += direction;
    }
    if (!checklist[index]) return 'END';

    this.index.set(index);
    const wert = checklist[index];
    let ist = this.edit_messung().messwerte[index]?.Ist;
    if (typeof ist == 'string' && !isNaN(+ist)) ist = +ist;

    this.erfassungForm.reset({
      ...this.edit_messung().messwerte[index],
      Ist: ist ?? this.defaultIst(wert),
    });

    this.isOpenModal.set(true);
  }

  private async calcAusfall(ist: number | 'Ja' | 'Nein' | 'n.g.', wert: ZählerWert) {
    if (ist == null || ist == 'n.g.') return null;

    let ausfall: AusfallStatus | 'max';
    let tol: ToleranzenPlus & ToleranzenMinus = {
      SR100minus: wert.SR100minus ?? NaN,
      SRlimminus: wert.SRlimminus ?? NaN,
      SRGminus: wert.SRGminus ?? NaN,
      SR100plus: wert.SR100plus ?? NaN,
      SRlimplus: wert.SRlimplus ?? NaN,
      SRGplus: wert.SRGplus ?? NaN,
    };

    if (wert.ZArt == ZArt.JaNein) ausfall = ist == 'Ja' ? AusfallStatus.iO : 'max';
    else {
      ist = +ist;
      wert.Soll = +wert.Soll;
      switch (wert.ZArt) {
        case ZArt.Numerisch: {
          tol.SRGminus = wert.Soll - tol.SRGminus;
          tol.SRlimminus = wert.Soll - tol.SRlimminus;
          tol.SR100minus = wert.Soll - tol.SR100minus;

          tol.SRGplus = wert.Soll + tol.SRGplus;
          tol.SRlimplus = wert.Soll + tol.SRlimplus;
          tol.SR100plus = wert.Soll + tol.SR100plus;
          //* weiter in ZwischenMinMax
        }
        case ZArt.ZwischenMinMax: {
          if (ist > tol.SRGplus || ist < tol.SRGminus) ausfall = AusfallStatus.XXX;
          else if (ist > tol.SRlimplus || ist < tol.SRlimminus) ausfall = AusfallStatus.XX;
          else if (ist > tol.SR100plus || ist < tol.SR100minus) ausfall = AusfallStatus.X;
          else ausfall = AusfallStatus.iO;
          break;
        }
        case ZArt.Mindestwert: {
          if (ist < tol.SRGminus) ausfall = AusfallStatus.XXX;
          else if (ist < tol.SRlimminus) ausfall = AusfallStatus.XX;
          else if (ist < tol.SR100minus) ausfall = AusfallStatus.X;
          else ausfall = AusfallStatus.iO;
          break;
        }
        case ZArt.Maximalwert: {
          if (ist > tol.SRGplus) ausfall = AusfallStatus.XXX;
          else if (ist > tol.SRlimplus) ausfall = AusfallStatus.XX;
          else if (ist > tol.SR100plus) ausfall = AusfallStatus.X;
          else ausfall = AusfallStatus.iO;
          break;
        }
        case ZArt.SollGreat: {
          if (ist > wert.Soll) ausfall = AusfallStatus.iO;
          else ausfall = 'max';
          break;
        }
        case ZArt.SollLess: {
          if (ist < wert.Soll) ausfall = AusfallStatus.iO;
          else ausfall = 'max';
          break;
        }
        case ZArt.SollEqGreat: {
          if (ist >= wert.Soll) ausfall = AusfallStatus.iO;
          else ausfall = 'max';
          break;
        }
        case ZArt.SollEqLess: {
          if (ist <= wert.Soll) ausfall = AusfallStatus.iO;
          else ausfall = 'max';
          break;
        }
      }
    }

    if (ausfall == 'max') {
      ausfall =
        wert.aktivSRG ? AusfallStatus.XXX
        : wert.aktivSRlimm ? AusfallStatus.XX
        : wert.aktivSR100 ? AusfallStatus.X
        : null;
    }
    return this.transformAusfall(ausfall);
  }

  private async transformAusfall(ausfall: AusfallStatus) {
    const tol = this.tol();
    switch (ausfall) {
      case AusfallStatus.X:
        return tol.TEXT_100_A;
      case AusfallStatus.XX:
        return tol.TEXT_L_A;
      case AusfallStatus.XXX:
        return tol.TEXT_G_A;
      case AusfallStatus.iO:
        return this.mrTranslate.transform('i.O.');
      default:
        return null;
    }
  }

  private defaultIst(wert: ZählerWert) {
    switch (wert?.ZArt) {
      case ZArt.Mindestwert:
        return wert?.SR100minus ?? wert?.SRlimminus ?? wert?.SRGminus ?? 'n.g.';
      case ZArt.Maximalwert:
        return wert?.SR100plus ?? wert?.SRlimplus ?? wert?.SRGplus ?? 'n.g.';
      case ZArt.SollGreat:
      case ZArt.SollLess:
      case ZArt.ZwischenMinMax:
        return 'n.g.';
      case ZArt.JaNein:
        return 'Ja';
      case ZArt.Numerisch:
      case ZArt.SollEqGreat:
      case ZArt.SollEqLess:
      default:
        return wert.Soll ?? 'n.g.';
    }
  }

  public excelExport() {
    type Grouped = {
      datum: string;
      ist: MesswertErfassung['Ist'];
      bem: string;
      ausfall: MesswertErfassung['Ausfall'];
    };

    const messungMap: Record<string, Grouped[]> = {};
    for (const group of this.messungen()) {
      for (const messwert of group.messwerte) {
        const key = messwert.Bezeichnung;
        messungMap[key] ??= [];
        messungMap[key].push({
          datum: group.pruefung.Column_Datum,
          ist: messwert.Ist,
          bem: messwert.Bemerkung,
          ausfall: messwert.Ausfall,
        });
      }
    }

    const toleranzen = this.tol();
    const tablerows = this.checklist().map(row => {
      const messwerte = messungMap[row.Code];

      const rowObj: Row = {
        Code: row.Code,
        Bezeichnung: row.Bezeichnung,
        Soll: row.Soll,
        'SR lim +': row.SRlimplus,
        'SR lim -': row.SRlimminus,
        Einheit: row.Einheit,
      };

      messwerte.forEach(mw => {
        rowObj[`Ist - ${mw.datum}`] = mw.ist;
        rowObj[`Bemerkung - ${mw.datum}`] = mw.bem;
        rowObj[`Tol. Verl. - ${mw.datum}`] = toleranzen[mw.ausfall];
      });
      return rowObj;
    });

    this.excelService.exportAsExcelFile(tablerows, 'zaehler');
  }
}

const createMessungShape = (): MessungGroup => ({
  pruefung: {
    P_ZDatum: BackendLocaleDatePipe.now,
    P_Bemerkung: null,
    P_ZUser: null,
    M_Device: null,
  },
  messwerte: [],
  unsaved: true,
});

const createEintrag = (model: MesswertErfassung, wert: ZählerWert): MesswertEintrag => ({
  ...model,
  Soll: wert.Soll,
  SR100minus: wert.SR100minus,
  SR100plus: wert.SR100plus,
  SRlimminus: wert.SRlimminus,
  SRlimplus: wert.SR100plus,
  SRGminus: wert.SRGminus,
  SRGplus: wert.SRGplus,
  Bezeichnung: wert.Code,
  isHeader: wert.ZType == ZType.Header,
  INFO: wert.Bezeichnung ? `${wert.Code}: ${wert.Bezeichnung}` : '',
  ZTYP:
    wert.ZType == ZType.Header ? 0
    : wert.ZArt == ZArt.JaNein ? 6
    : 1,
});
